import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/lib/constants';
import { toast } from 'sonner';
import type { CreateExpenseParams } from '@/types/domain.types';

import { logger } from '@/lib/logger'

// Create expense with offline support - saves to localStorage
export function useCreateOfflineExpense() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createOfflineExpense'],
    mutationFn: async (params: CreateExpenseParams) => {
      logger.debug('[useOfflineExpenses] Creating expense with params:', { params });

      // Calculate the expense date
      let expenseDate: Date
      if (params.date) {
        const d = new Date(params.date)
        d.setHours(12, 0, 0, 0) // Set to noon local time to avoid timezone issues
        expenseDate = d
      } else {
        expenseDate = new Date()
      }

      const offlineExpense = {
        id: `offline-${Date.now()}`,
        group_id: params.groupId,
        category_id: params.categoryId,
        funding_pool_id: params.fundingPoolId,
        use_group_pool: params.useGroupPool,
        description: params.description || '',
        amount: params.amount,
        currency_code: params.currency,
        paid_by: params.paidBy,
        created_by: params.createdBy,
        date: expenseDate.toISOString(),
        created_at: expenseDate.toISOString(),
        synced: false,
        _local: {
          version: 1,
          lastSynced: null,
          isDirty: true,
          isDeleted: false,
          localOnly: true
        }
      };

      // Save to localStorage
      const savedExpenses = JSON.parse(localStorage.getItem('offline-expenses') || '[]');
      savedExpenses.push(offlineExpense);
      localStorage.setItem('offline-expenses', JSON.stringify(savedExpenses));
      logger.debug('[useOfflineExpenses] Saved to localStorage. Total:', savedExpenses.length);

      // Update the transactions query cache for immediate UI feedback
      queryClient.setQueryData(
        [QUERY_KEYS.TRANSACTIONS, params.groupId],
        (oldData: any) => {
          if (!oldData) return oldData;

          const newTransaction = {
            ...offlineExpense,
            type: 'expense' as const,
            date: offlineExpense.created_at,
            category: {
              id: offlineExpense.category_id,
              group_id: params.groupId,
              name: 'Pending Sync',
              emoji: '⏳'
            },
            user: {
              id: offlineExpense.paid_by,
              name: 'You',
              email: 'offline@local'
            },
            is_offline: true
          };

          return {
            ...oldData,
            transactions: [newTransaction, ...(oldData.transactions || [])]
          };
        }
      );

      // Invalidate other queries so dashboard refreshes
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPENSES, params.groupId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GROUP_BALANCES, params.groupId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CATEGORY_BALANCES, params.groupId] });

      return offlineExpense;
    },

    onSuccess: () => {
      toast.success('Expense saved offline! Will sync when online.');
    },

    onError: (error) => {
      logger.error('Failed to create expense:', error);
      toast.error('Failed to create expense. Please try again.');
    }
  });

  return mutation;
}
